
import React from 'react';
import { ViewState, Language, UserRole } from '../types';
import { LayoutDashboard, Users, FileText, BarChart3, Settings, ShieldCheck, UserCircle, RefreshCcw, Briefcase, PlusCircle, List, MessageSquare, X, HardDrive, DollarSign, UserCog, Share2, Gavel, Users2 } from 'lucide-react';
import { TRANSLATIONS } from '../translations';

interface SidebarProps {
  currentView: ViewState;
  onChangeView: (view: ViewState) => void;
  language: Language;
  userRole: UserRole;
  onToggleRole: () => void;
  isOpen: boolean; // Mobile open state
  onClose: () => void; // Mobile close handler
}

export const Sidebar: React.FC<SidebarProps> = ({ 
  currentView, 
  onChangeView, 
  language, 
  userRole, 
  onToggleRole,
  isOpen,
  onClose
}) => {
  const t = TRANSLATIONS[language];
  
  // Define menu items based on role
  const projectItems = [
    { id: ViewState.DASHBOARD, label: t.dashboard, icon: LayoutDashboard },
    { id: ViewState.MARKETPLACE, label: t.marketplace, icon: Users },
    { id: ViewState.SQUADS, label: t.squads, icon: Users2 }, // New Squads Item
    { id: ViewState.MY_CAMPAIGNS, label: t.myCampaigns, icon: List }, 
    { id: ViewState.ORDERS, label: t.contracts, icon: FileText },
    { id: ViewState.CHAT, label: t.messages, icon: MessageSquare },
    { id: ViewState.REFERRALS, label: t.referrals, icon: Share2 }, // Projects can refer too
    { id: ViewState.ANALYTICS, label: t.analytics, icon: BarChart3 },
    { id: ViewState.SETTINGS, label: t.settings, icon: Settings },
  ];

  const kolItems = [
    { id: ViewState.DASHBOARD, label: t.dashboard, icon: LayoutDashboard },
    { id: ViewState.KOL_PROFILE, label: t.myProfile, icon: UserCircle },
    { id: ViewState.PROJECT_MARKETPLACE, label: t.findProjects, icon: Briefcase },
    { id: ViewState.SQUADS, label: t.squads, icon: Users2 }, // Added Squads Item for KOLs
    { id: ViewState.ORDERS, label: t.contracts, icon: FileText },
    { id: ViewState.CHAT, label: t.messages, icon: MessageSquare },
    { id: ViewState.REFERRALS, label: t.referrals, icon: Share2 },
    { id: ViewState.ANALYTICS, label: t.analytics, icon: BarChart3 },
    { id: ViewState.SETTINGS, label: t.settings, icon: Settings },
  ];

  const adminItems = [
    { id: ViewState.ADMIN_DASHBOARD, label: t.adminDashboard, icon: HardDrive },
    { id: ViewState.DISPUTES, label: t.disputeCenter, icon: Gavel }, // New Dispute Item
    { id: ViewState.ORDERS, label: t.contracts, icon: FileText },
    { id: ViewState.MARKETPLACE, label: t.userManagement, icon: UserCog },
    { id: ViewState.ANALYTICS, label: t.financials, icon: DollarSign },
    { id: ViewState.SETTINGS, label: t.settings, icon: Settings },
  ];

  const menuItems = userRole === UserRole.PROJECT ? projectItems : (userRole === UserRole.KOL ? kolItems : adminItems);

  // Helper to get next role label for the toggle button
  const getNextRoleLabel = () => {
    if (userRole === UserRole.PROJECT) return t.switchToKol;
    if (userRole === UserRole.KOL) return t.switchToAdmin;
    return t.switchToProject;
  };

  const getRoleBadgeColor = () => {
      if (userRole === UserRole.ADMIN) return 'bg-amber-500';
      if (userRole === UserRole.PROJECT) return 'bg-indigo-500';
      return 'bg-rose-500';
  };

  const getRoleLabel = () => {
      if (userRole === UserRole.ADMIN) return 'Admin';
      if (userRole === UserRole.PROJECT) return 'Project';
      return 'Creator';
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-20 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <div className={`w-64 bg-slate-900 border-r border-slate-800 h-screen fixed left-0 top-0 flex flex-col z-30 transition-transform duration-300 md:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getRoleBadgeColor()}`}>
              <ShieldCheck className="text-white w-5 h-5" />
            </div>
            <div>
              <span className="text-xl font-bold text-white tracking-tight block leading-none">LinkKol</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest">
                {getRoleLabel()}
              </span>
            </div>
          </div>
          {/* Mobile Close Button */}
          <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onChangeView(item.id);
                  onClose(); // Close on mobile selection
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-600/20'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-4">
          <button 
              onClick={() => {
                onToggleRole();
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium border border-slate-700 transition-colors"
          >
              <RefreshCcw size={14} />
              {getNextRoleLabel()}
          </button>

          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">{t.yourBalance}</p>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-200">
                  {userRole === UserRole.ADMIN ? '∞' : (userRole === UserRole.PROJECT ? '12,450 USDT' : '2,450 USDT')}
              </span>
              <div className={`w-2 h-2 rounded-full ${getRoleBadgeColor()}`}></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
